import {Helper} from "@nolimitcity/promo-panel/bin/utils/Helper";
import {WINTYPE} from "@nolimitcity/promo-panel/bin/enums/ASEnums";
import {
    ActionSpinOptions,
    ActionSpinSound,
    ASWinData,
    PromoPanelGameConfiguration
} from "@nolimitcity/promo-panel/bin/interfaces/ASInterfaces";
import {Game} from "./Game";
import {BonusBuyPopUpContent} from "@nolimitcity/promo-panel/bin/feature/generic/BonusBuyPopUpContent";
import {Mental2GameData} from "./server/Data";
import {GameFeatureMode} from "./enums/Enum";
import {GameUtils} from "./utils/GameUtils";
import {gsap} from "gsap";
import {SimpleReelModel} from "../core/reel/simplereel/SimpleReelModel";
import {SymbolContainer} from "./reels/SymbolContainer";
import {Sound} from "./sound/Sound";

export class GamePromoPanelConfig implements PromoPanelGameConfiguration {
    private featureName: string;

    public getActionSpinOptions(): ActionSpinOptions {
        return {
            gameName: "Mental2",
            hasActionSpin: true,
            graphics: {backgroundTextureName: "floor_0"},

            getParsedData: (response: Mental2GameData, bet: number): ASWinData => {

                const parsedData: ASWinData = {
                    totalWin: response.accumulatedRoundWin,
                    wasFeatureBuy: response.wasFeatureBuy,
                    freeSpinTriggeredThisSpin: GameUtils.isBonusTriggeredThisSpin(response.mode, response.nextMode),
                    totalSpinWinnings: this.getSpinWin(response),
                    isBigWin: this.isBigWin(response, bet),
                    winType: WINTYPE.NORMAL,
                    featureName: this.getFeatureName(response),
                    isRoundComplete: this.isRoundComplete(response),
                    isBonusEnd: this.isBonusEnd(response),
                    isWinCapHit: response.brokeTheBank,
                }
                return parsedData;
            }
        };
    }

    public replaceBonusBuyPopUpContent(orgContent: BonusBuyPopUpContent): PIXI.Container {
        // You can modify the orgContent to your liking, like in the simple text replace below. All params is public in orgContent, it's basically a PIXI container with some stuff in.
        // Or you can make you your own container with everything custom. You don't need to return a BonusBuyPopUpContent, it's enough with a container.
        // Use the priceString if you change the price text, it's already formatted correctly.
        // if (orgContent.featureName === "GOD_MODE") {
        //     const warningText: Label = new Label(Game.apiPlugin.translations.translate("Warning: 1 Spin!"), orgContent.textLabel.getStyleClone()),
        //         scale: number = Math.min(560 / warningText.width, 1);
        //     warningText.scale.set(scale);
        //     warningText.anchor.set(0.5);
        //     warningText.position.set(0, 420);
        //     orgContent.addChild(warningText);
        // }

        return orgContent;
    }

    public onActionSpinPlaySound(trigger: ActionSpinSound): void {
        if (trigger === ActionSpinSound.WIN) {
            Game.soundPlayer.playSound(Sound.AS_WIN, 1);
        } else if (trigger === ActionSpinSound.BIG_WIN) {
            Game.soundPlayer.playSound(Sound.AS_WIN_BIG);
        }
    }

    public onPlayBonusInGame(data: Mental2GameData): void {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        let triggeredView;
        if (data.nextMode === GameFeatureMode.FREESPIN_SUPER) {
            triggeredView = Game.sfsIntroView;
        } else if (data.nextMode === GameFeatureMode.FREESPIN_COOL) {
            triggeredView = Game.cfsIntroView;
        } else {
            triggeredView = Game.fsIntroView;
        }
        tl.add(() => {
            Game.idleNormalPresentation.skip();
        });
        tl.add(() => {
            if (data.reelsEnteringFreespin) {
                Game.reelView.hide(0.001);
                data.reelsEnteringFreespin.forEach((reels: string[], reelId: number) => {
                    const reelModel: SimpleReelModel = Game.reelView.reelModels[reelId];
                    reels.forEach((symName: string, rowId: number) => {
                        const symContainer: SymbolContainer = reelModel.getStopSymbolAt(rowId)?.positionable as SymbolContainer;
                        if (symContainer) {
                            symContainer.split(1, symName);
                        }
                    });
                });
                Game.model.onGame(data);
                Game.keypadPlugin.setZeroBetSpinCounter(data.freespinsLeft);
                Game.keypadPlugin.setWin(data.accumulatedRoundWin, true, Game.isWinBelowStake());
            }
        });
        tl.add(triggeredView.show(() => {
            Game.apiPlugin.slotStates.resetGameStartedAndGameServerFlag();

            setTimeout(() => {
                Game.gameFlowController.start()
            }, 300);
        }));
    }

    private isBigWin(response: Mental2GameData, bet: number): boolean {
        const win: number = this.getSpinWin(response);

        // #355 -> BOOSTED_BET_4 is for xMental
        if (response.wasFeatureBuy || Game.buyFeatureName === "BOOSTED_BET_4" || Game.buyFeatureName === "BOOSTED_BET_3") {
            const winRatio: number = Helper.ceilToDecimals((win / Game.getBuyFeaturePrice()), 2);

            return (winRatio >= 1 && !Game.isWinBelowStake());
        }

        return (Helper.ceilToDecimals((win / bet), 2) >= 15);
    }

    private getSpinWin(response: Mental2GameData): number {
        return response.totalSpinWinnings;
    }

    private getFeatureName(response: Mental2GameData): string {
        if (response.nextMode === GameFeatureMode.FREESPIN || response.mode === GameFeatureMode.FREESPIN) {
            return "BLOODLETTING SPINS";
        } else if (response.nextMode === GameFeatureMode.FREESPIN_COOL || response.mode === GameFeatureMode.FREESPIN_COOL) {
            return "SURGERY SPINS";
        } else if (response.nextMode === GameFeatureMode.FREESPIN_SUPER || response.mode === GameFeatureMode.FREESPIN_SUPER) {
            return "EXPERIMENTAL SPINS";
        }
        return "";
    }

    private isRoundComplete(response: Mental2GameData): boolean {
        //return response.nextMode == GameFeatureMode.NORMAL;
        return ([GameFeatureMode.FREESPIN, GameFeatureMode.FREESPIN_COOL, GameFeatureMode.FREESPIN_SUPER, GameFeatureMode.NORMAL].indexOf(response.nextMode as GameFeatureMode) > -1);
    }

    private isBonusEnd(response: Mental2GameData): boolean {
        return ((response.nextMode === GameFeatureMode.NORMAL) && (response.mode === GameFeatureMode.FREESPIN || response.mode === GameFeatureMode.FREESPIN_COOL || response.mode === GameFeatureMode.FREESPIN_SUPER));
    }
}